import axios from 'axios';

const getBaseUrl = () => {
  const configuredBase =
    import.meta.env.VITE_API_BASE_URL ||
    (window as any).__API_BASE_URL__ ||
    '';

  if (configuredBase) {
    return configuredBase.endsWith('/')
      ? configuredBase.slice(0, -1)
      : configuredBase;
  }

  return '/api/v1';
};

const API_BASE_URL = getBaseUrl();

if (import.meta.env.DEV) {
  console.info('[API] Base URL:', API_BASE_URL);
}

// Rastrear quando o login foi feito para evitar invalidar token imediatamente após login
let lastLoginTime: number | null = null;
const LOGIN_GRACE_PERIOD = 5000; // 5 segundos após login, não invalidar token (aumentado de 3s para 5s)

// Função para registrar quando login é feito
export const setLastLoginTime = () => {
  lastLoginTime = Date.now();
  console.log('[API] Login registrado, período de graça ativado por', LOGIN_GRACE_PERIOD, 'ms');
};

// Função para verificar se estamos no período de graça
export const isInLoginGracePeriod = (): boolean => {
  if (lastLoginTime === null) return false;
  const inGracePeriod = Date.now() - lastLoginTime < LOGIN_GRACE_PERIOD;
  if (inGracePeriod) {
    const remaining = LOGIN_GRACE_PERIOD - (Date.now() - lastLoginTime);
    console.log('[API] No período de graça após login,', Math.round(remaining), 'ms restantes');
  }
  return inGracePeriod;
};

/** Invalida sessão local em 401 — respeita período de graça pós-login. */
export const invalidateAuthOnUnauthorized = (error: { response?: { status?: number }; config?: { url?: string } }, apiLabel = 'API'): void => {
  if (error.response?.status !== 401) return;

  const isLoginPage = window.location.pathname === '/login';
  const isProfileCheck = error.config?.url?.includes('/users/profile');

  if (isInLoginGracePeriod()) {
    console.warn(`[${apiLabel}] 401 durante período de graça após login, ignorando invalidação de token`);
    return;
  }

  if (isLoginPage || isProfileCheck) return;

  const currentToken = localStorage.getItem('token');
  if (currentToken) {
    localStorage.removeItem('token');
    localStorage.removeItem('default_company_id');
    window.dispatchEvent(new CustomEvent('auth:token-invalid'));
  }
};

const attachUnauthorizedInterceptor = (instance: typeof authAPI, label: string) => {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      invalidateAuthOnUnauthorized(error, label);
      return Promise.reject(error);
    },
  );
};

// Configurar axios para usar o token JWT
// URLs para Kubernetes (via Ingress) ou custom base
const authAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const clientAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const employeeAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const serviceAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const productAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const financialAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const notificationAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token nas requisições
authAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

clientAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

employeeAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

serviceAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

productAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

financialAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

notificationAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

attachUnauthorizedInterceptor(authAPI, 'AuthAPI');
attachUnauthorizedInterceptor(clientAPI, 'ClientAPI');
attachUnauthorizedInterceptor(employeeAPI, 'EmployeeAPI');
attachUnauthorizedInterceptor(serviceAPI, 'ServiceAPI');
attachUnauthorizedInterceptor(productAPI, 'ProductAPI');
attachUnauthorizedInterceptor(financialAPI, 'FinancialAPI');
attachUnauthorizedInterceptor(notificationAPI, 'NotificationAPI');

export { authAPI, clientAPI, employeeAPI, serviceAPI, productAPI, financialAPI, notificationAPI };
